import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { CalendarIcon, Loader2 } from 'lucide-react';

interface AddCalendarEventModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEventAdd: (eventData: CalendarEventData) => void;
  isAdding: boolean;
  preselectedCustomer?: {
    id: string;
    name: string;
  };
}

interface CalendarEventData {
  title: string;
  description?: string;
  event_type: string;
  start_date: string;
  start_time?: string;
  customer_id?: string;
  priority: string;
  category: string;
}

const eventTypes = [
  { value: 'appointment', label: 'Randevu' },
  { value: 'follow_up', label: 'Takip Görevi' },
  { value: 'meeting', label: 'Toplantı' },
  { value: 'task', label: 'Görev' },
  { value: 'call', label: 'Arama' },
  { value: 'other', label: 'Diğer' },
];

const priorities = [
  { value: 'low', label: 'Düşük' },
  { value: 'medium', label: 'Orta' },
  { value: 'high', label: 'Yüksek' },
  { value: 'urgent', label: 'Acil' },
];

const categories = [
  { value: 'sales', label: 'Satış' },
  { value: 'support', label: 'Destek' },
  { value: 'marketing', label: 'Pazarlama' },
  { value: 'administrative', label: 'İdari' },
  { value: 'other', label: 'Diğer' },
];

export const AddCalendarEventModal = ({ 
  open, 
  onOpenChange, 
  onEventAdd, 
  isAdding,
  preselectedCustomer
}: AddCalendarEventModalProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [eventType, setEventType] = useState('appointment');
  const [date, setDate] = useState<Date>();
  const [time, setTime] = useState('');
  const [priority, setPriority] = useState('medium');
  const [category, setCategory] = useState('sales');

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setEventType('appointment');
    setDate(undefined);
    setTime('');
    setPriority('medium');
    setCategory('sales');
  };

  useEffect(() => {
    if (open && preselectedCustomer) {
      setTitle(`${preselectedCustomer.name} - `);
    }
  }, [open, preselectedCustomer]);

  const handleSubmit = () => {
    if (!title.trim() || !date) return;

    const eventData: CalendarEventData = {
      title: title.trim(),
      description: description.trim() || undefined,
      event_type: eventType,
      start_date: format(date, 'yyyy-MM-dd'),
      start_time: time || undefined,
      customer_id: preselectedCustomer?.id,
      priority,
      category,
    };

    onEventAdd(eventData);
  };

  const handleCancel = () => {
    resetForm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Takvime Etkinlik Ekle</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Event Title */}
          <div className="space-y-2">
            <Label>Etkinlik Başlığı</Label>
            <Input
              placeholder="Etkinlik başlığı"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Event Type */}
          <div className="space-y-2">
            <Label>Etkinlik Türü</Label>
            <Select value={eventType} onValueChange={setEventType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {eventTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tarih</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "dd MMM yyyy", { locale: tr }) : "Tarih seçin"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                    locale={tr}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Saat (Opsiyonel)</Label>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>
          </div>

          {/* Priority and Category */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Öncelik</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {priorities.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Kategori</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Açıklama (Opsiyonel)</Label>
            <Textarea
              placeholder="Etkinlik açıklaması"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={handleCancel} className="flex-1">
              İptal
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={!title.trim() || !date || isAdding}
              className="flex-1"
            >
              {isAdding ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Ekleniyor...
                </>
              ) : (
                'Etkinliği Ekle'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
